import type { ReactNode } from "react"

type AlignType = "left" | "center" | "right"

interface TableColumn<Row> {
  /** 데이터 필드명 */
  dataField: keyof Row
  /** 컬럼 헤더 텍스트 */
  caption: string
  alignment?: AlignType
  /** px 또는 Tailwind 클래스 */
  width?: number | string
  /** 셀 커스텀 렌더 */
  cellTemplate?: (row: Row) => ReactNode
  /** 행 클릭 시 버튼이 될 제목 컬럼 (onRowClick 사용 시 하나 지정) */
  isTitle?: boolean
}

interface DataTableProps<Row> {
  rowIdField: keyof Row
  dataSource: Row[]
  columns: TableColumn<Row>[]
  /** 표 제목 — 스크린리더용 caption */
  caption: string
  emptyMessage?: string
  onRowClick?: (row: Row) => void
}

function alignClassOf(alignment?: AlignType): string {
  if (alignment === "center") return "text-center"
  if (alignment === "right") return "text-right"
  return "text-left"
}

function widthStyleOf(width?: number | string) {
  if (typeof width === "number") return { width: `${width}px` }
  if (typeof width === "string") return { width }
  return undefined
}

/**
 * 폭을 지정하지 않은 열의 최소 폭.
 *
 * 지정 폭의 합이 표 최소 폭(`min-w-*`)에 가까우면 남은 열에 줄 공간이 없어
 * 「2026 국제 격투스포츠 대회」가 한두 글자씩 끊겨 내려간다 (모바일 폭에서 특히).
 * 이 최소 폭을 걸어 두면 브라우저가 지정 폭 열을 줄여 자리를 만든다.
 */
const FLEXIBLE_COLUMN_MIN_WIDTH = "min-w-36"

function cellClassOf<Row>(column: TableColumn<Row>): string {
  const alignClass = alignClassOf(column.alignment)

  return column.width === undefined
    ? `${alignClass} ${FLEXIBLE_COLUMN_MIN_WIDTH}`
    : alignClass
}

export default function DataTable<Row>({
  rowIdField,
  dataSource,
  columns,
  caption,
  emptyMessage = "등록된 데이터가 없습니다.",
  onRowClick,
}: Readonly<DataTableProps<Row>>) {
  const isRowClickable = Boolean(onRowClick)

  function onClickRow(row: Row) {
    onRowClick?.(row)
  }

  return (
    <table className="app-table app-table-hover min-w-[880px]">
      <caption className="sr-only">{caption}</caption>

      <colgroup>
        {columns.map((column) => (
          <col
            key={String(column.dataField)}
            style={widthStyleOf(column.width)}
          />
        ))}
      </colgroup>

      <thead>
        <tr>
          {columns.map((column) => (
            <th
              key={String(column.dataField)}
              scope="col"
              className={cellClassOf(column)}
            >
              {column.caption}
            </th>
          ))}
        </tr>
      </thead>

      <tbody>
        {dataSource.length === 0 ? (
          <tr>
            <td
              colSpan={columns.length}
              aria-live="polite"
              className="app-txt-body2 px-4 py-12 text-center text-app-text-200"
            >
              {emptyMessage}
            </td>
          </tr>
        ) : (
          dataSource.map((row) => (
            <tr key={String(row[rowIdField])}>
              {columns.map((column) => {
                const value = column.cellTemplate
                  ? column.cellTemplate(row)
                  : String(row[column.dataField] ?? "")

                return (
                  <td
                    key={String(column.dataField)}
                    className={cellClassOf(column)}
                  >
                    {isRowClickable && column.isTitle ? (
                      <button
                        type="button"
                        onClick={() => onClickRow(row)}
                        className="focus-ring w-full cursor-pointer text-left"
                      >
                        {value}
                      </button>
                    ) : (
                      value
                    )}
                  </td>
                )
              })}
            </tr>
          ))
        )}
      </tbody>
    </table>
  )
}

export type { TableColumn, DataTableProps, AlignType }
